import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Tooltip, Typography } from '@mui/material';

/**
 * Reusable utilities for determining and displaying open source status
 */
export const OpenSourceStatus = {
  /**
   * Determine if a model should be considered open source based on its source_type
   * @param {string} sourceType - The source_type of the AI model
   * @returns {Object} Object containing status information
   */
  getStatus: (sourceType) => {
    // Return different status for different source types
    switch (sourceType) {
      case 'open_source':
        return {
          isOpen: true,
          label: 'Open Source',
          icon: <CheckCircleIcon color="success" />,
          color: 'success',
          description: 'This model is fully open source'
        };
      case 'partial_open':
        return {
          isOpen: true,
          label: 'Partially Open',
          icon: <CheckCircleIcon color="success" />,
          color: 'success',
          description: 'This model is partially open source'
        };
      case 'source_available':
        return {
          isOpen: false,
          label: 'Source Available',
          icon: <CancelIcon color="warning" />,
          color: 'warning',
          description: 'Source is available but with restricted licensing'
        };
      case 'closed_source':
        return {
          isOpen: false,
          label: 'Closed Source',
          icon: <CancelIcon color="error" />,
          color: 'error',
          description: 'This model is not open source'
        };
      case 'research_only':
        return {
          isOpen: false,
          label: 'Research Only',
          icon: <CancelIcon color="warning" />,
          color: 'warning',
          description: 'Available for academic/non-commercial use only'
        };
      case 'no_data':
      default:
        return {
          isOpen: null,
          label: 'No Data',
          icon: <HelpOutlineIcon color="disabled" />,
          color: 'default',
          description: 'Open source status unknown'
        };
    }
  },

  /**
   * Component to display open source status with icon
   * @param {Object} props - Component props
   * @param {string} props.sourceType - The source_type of the AI model
   * @returns {JSX.Element} - Rendered status component
   */
  StatusDisplay: ({ sourceType }) => {
    const status = OpenSourceStatus.getStatus(sourceType);

    return (
      <Tooltip title={status.description}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {status.icon}
          <Typography variant="body2">{status.label}</Typography>
        </Box>
      </Tooltip>
    );
  },

  /**
   * Component to display just the icon for open source status
   * @param {Object} props - Component props
   * @param {string} props.sourceType - The source_type of the AI model
   * @returns {JSX.Element} - Rendered icon component
   */
  IconOnly: ({ sourceType }) => {
    const status = OpenSourceStatus.getStatus(sourceType);

    return (
      <Tooltip title={`${status.label}: ${status.description}`}>
        {status.icon}
      </Tooltip>
    );
  }
};

export default OpenSourceStatus;
